import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useDeliveryLocation } from '../context/DeliveryLocationContext';
import * as cartApi from '../api/endpoints/cart';
import { isStaleLocationError } from '../utils/staleLocation';

const IDLE = Object.freeze({ status: 'idle', quote: null, error: null, key: null });

/**
 * The coupon intent as the backend will read it: /cart/quote trims and
 * upper-cases `couponCode`, and treats a blank one as none. Mirrored here only
 * so two spellings of one code are one quote identity; whether the code is
 * valid, and what it takes off, stays entirely the backend's answer.
 */
export const normalizeCouponCode = (code) =>
  typeof code === 'string' && code.trim() ? code.trim().toUpperCase() : null;

/** What a quote is asked for: product identity and quantity, nothing else. */
export const quoteLinesFor = (items) =>
  items.map((item) => ({ productId: String(item.productId), quantity: Number(item.quantity) }));

/** Order-independent identity of those lines, for quote keys. */
export const cartKeyFor = (lines) =>
  lines.map((line) => `${line.productId}:${line.quantity}`).sort().join('|');

/**
 * The backend's authoritative price for the cart, as it stands right now.
 *
 * The cart (CartContext) says WHAT the customer wants and how many; this hook
 * asks POST /cart/quote what that costs at the browsing location. Nothing here
 * prices anything itself.
 *
 * A quote is requested only when the backend can give one: a signed-in
 * customer (the endpoint requires it), a non-empty cart, and a browsing
 * location an outlet serves (storefrontStatus 'ready'). It is re-requested
 * when the cart lines (product + quantity), the effective geography
 * (locationVersion) or the SUBMITTED coupon code change, or on retry — never
 * because a same-geography service-context refresh or a label change happened,
 * and never while a customer is still typing a code (the caller passes only
 * the code they applied).
 *
 * The returned quote always belongs to the CURRENT cart, location and coupon:
 * it is keyed by all three, and a quote for any other key is treated as absent during the
 * very render the inputs change — so a stale quote or quoteToken can never be
 * read, even before the new request has gone out. Late responses for older
 * inputs are dropped by request id, and a response priced for a previous
 * browsing location is rejected by the API client (geographySensitive).
 *
 * Every quote also asks for `availableCoupons` (the listed codes this basket
 * could use, as suggestions); that flag is constant, so it is not identity.
 *
 * status: idle | loading | ready | error
 * quoteToken: the backend's signed token, opaque — only ever passed back.
 * couponCode: the normalized coupon the current quote is (being) priced with.
 */
const useCartQuote = ({ couponCode: requestedCoupon = null } = {}) => {
  const { items } = useCart();
  const { authStatus } = useAuth();
  const { storefrontStatus, locationVersion } = useDeliveryLocation();

  const lines = useMemo(() => quoteLinesFor(items), [items]);
  const cartKey = useMemo(() => cartKeyFor(lines), [lines]);
  const couponCode = normalizeCouponCode(requestedCoupon);
  const canQuote = authStatus === 'authenticated' && storefrontStatus === 'ready' && lines.length > 0;
  // Cart keys hold only ids, quantities, ':' and '|', so the coupon — last —
  // can never make two different inputs share a key.
  const key = canQuote ? `${locationVersion}#${cartKey}#${couponCode || ''}` : null;

  const [state, setState] = useState(IDLE);
  const [attempt, setAttempt] = useState(0);
  const requestIdRef = useRef(0);
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const couponRef = useRef(couponCode);
  couponRef.current = couponCode;

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    if (!key) {
      setState(IDLE);
      return undefined;
    }
    setState({ status: 'loading', quote: null, error: null, key });
    cartApi
      .quoteCart(linesRef.current, { couponCode: couponRef.current, includeAvailableCoupons: true })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        setState({ status: 'ready', quote: response?.data || null, error: null, key });
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current || isStaleLocationError(error)) return;
        setState({ status: 'error', quote: null, error, key });
      });
    return () => {
      // The inputs moved on (or the page closed): this answer must not land.
      if (requestIdRef.current === requestId) requestIdRef.current += 1;
    };
  }, [key, attempt]);

  const refresh = useCallback(() => setAttempt((count) => count + 1), []);

  // A quote for other inputs is never current, not even for one render.
  const current = key && state.key === key ? state : { ...IDLE, status: key ? 'loading' : 'idle' };
  const quote = current.status === 'ready' ? current.quote : null;

  return {
    status: current.status,
    quote,
    quoteToken: quote?.quoteToken || null,
    error: current.status === 'error' ? current.error : null,
    // What the current quote was (or is being) priced for.
    quoteKey: key,
    couponCode,
    canQuote,
    refresh,
  };
};

export default useCartQuote;
